import { useCallback, useEffect, useMemo, useState } from "react";
import StatusBadge from "../../intelligence/StatusBadge";
import {
  getAdminImageHealth,
  getAdminImageHealthDetail,
  patchAdminImageHealth,
  validateAdminImageHealth,
  validateAdminImageRecord,
} from "../../../../services/midpointApi";

function healthTone(status) {
  switch (status) {
    case "HEALTHY":
      return "success";
    case "BROKEN":
    case "INVALID_URL":
    case "BROWSER_FAILED":
    case "BROKEN_CANDIDATE":
      return "danger";
    case "NEEDS_RECHECK":
      return "warning";
    case "BLOCKED":
      return "warning";
    case "TIMEOUT":
      return "medium";
    default:
      return "neutral";
  }
}

const ImageHealthPage = ({ onBack, onOpenSourceQuality, onOpenImageCache }) => {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState(null);
  const [validating, setValidating] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadList = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const data = await getAdminImageHealth();
      setSources(data.sources || []);
    } catch (err) {
      setError(err.message || "Unable to load image health.");
      setSources([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const summary = useMemo(() => {
    const totals = sources.reduce(
      (accumulator, source) => ({
        total: accumulator.total + (source.totalImages || 0),
        healthy: accumulator.healthy + (source.healthyImages || 0),
        broken: accumulator.broken + (source.brokenImages || 0),
        unknown: accumulator.unknown + (source.unknownImages || 0),
        blocked: accumulator.blocked + (source.blockedImages || 0),
        unchecked: accumulator.unchecked + (source.uncheckedImages || 0),
        browserFailures: accumulator.browserFailures + (source.totalBrowserFailures || 0),
        needsRecheck: accumulator.needsRecheck + (source.imagesNeedingRecheck || 0),
      }),
      {
        total: 0,
        healthy: 0,
        broken: 0,
        unknown: 0,
        blocked: 0,
        unchecked: 0,
        browserFailures: 0,
        needsRecheck: 0,
      }
    );

    const scored = sources.filter((source) => source.imageHealthScore != null);
    const average =
      scored.length > 0
        ? Math.round(
            scored.reduce((sum, source) => sum + source.imageHealthScore, 0) / scored.length
          )
        : null;

    return { ...totals, average, sourceCount: sources.length };
  }, [sources]);

  const openDetail = async (sourceKey) => {
    setSelectedKey(sourceKey);
    setDetailLoading(true);
    setDetail(null);

    try {
      const data = await getAdminImageHealthDetail(sourceKey, { limit: 80 });
      setDetail(data);
    } catch (err) {
      setToast({ message: err.message, tone: "error" });
    } finally {
      setDetailLoading(false);
    }
  };

  const handleValidateAll = async () => {
    try {
      setValidating(true);
      await validateAdminImageHealth({ limit: 200 });
      await loadList();
      setToast({ message: "Image validation started for all sources.", tone: "success" });
    } catch (err) {
      setToast({ message: err.message, tone: "error" });
    } finally {
      setValidating(false);
    }
  };

  const handleValidateSource = async (sourceKey) => {
    try {
      setValidating(true);
      await validateAdminImageHealth({ sourceKey, limit: 200 });
      await loadList();
      if (selectedKey === sourceKey) {
        await openDetail(sourceKey);
      }
      setToast({ message: "Source images validated.", tone: "success" });
    } catch (err) {
      setToast({ message: err.message, tone: "error" });
    } finally {
      setValidating(false);
    }
  };

  const handleRecheckImage = async (imageId) => {
    try {
      setValidating(true);
      await validateAdminImageRecord(imageId);
      if (selectedKey) {
        await openDetail(selectedKey);
      }
      await loadList();
      setToast({ message: "Image rechecked.", tone: "success" });
    } catch (err) {
      setToast({ message: err.message, tone: "error" });
    } finally {
      setValidating(false);
    }
  };

  const handleClearBrowserFailures = async (imageId) => {
    try {
      await patchAdminImageHealth(imageId, { clear_browser_failures: true });
      if (selectedKey) {
        await openDetail(selectedKey);
      }
      await loadList();
      setToast({ message: "Browser failure counts cleared.", tone: "success" });
    } catch (err) {
      setToast({ message: err.message, tone: "error" });
    }
  };

  const handleOverride = async (imageId, healthStatus) => {
    try {
      await patchAdminImageHealth(imageId, { health_status: healthStatus });
      if (selectedKey) {
        await openDetail(selectedKey);
      }
      await loadList();
      setToast({ message: `Image marked ${healthStatus}.`, tone: "success" });
    } catch (err) {
      setToast({ message: err.message, tone: "error" });
    }
  };

  return (
    <div className="admin-page image-health-page source-quality-page">
      <header className="admin-page__header">
        <div>
          <button type="button" className="admin-back-link" onClick={onBack}>
            ← Back to map
          </button>
          <p className="eyebrow">Administration · Real Estate</p>
          <h1>Image Health</h1>
          <p className="admin-page__lede">
            Validate broker image URLs, track broken or blocked assets, and keep public listings
            on healthy media.
          </p>
        </div>
        <div className="admin-page__actions">
          <button type="button" onClick={handleValidateAll} disabled={validating}>
            {validating ? "Validating…" : "Validate all"}
          </button>
        </div>
      </header>

      {toast ? (
        <p className={`admin-toast admin-toast--${toast.tone}`} role="status">
          {toast.message}
        </p>
      ) : null}

      <section className="source-quality-summary">
        <article className="panel-card">
          <p className="eyebrow">Images tracked</p>
          <strong className="source-quality-summary__value">{summary.total}</strong>
        </article>
        <article className="panel-card">
          <p className="eyebrow">Healthy</p>
          <strong className="source-quality-summary__value">{summary.healthy}</strong>
        </article>
        <article className="panel-card">
          <p className="eyebrow">Broken</p>
          <strong className="source-quality-summary__value">{summary.broken}</strong>
        </article>
        <article className="panel-card">
          <p className="eyebrow">Browser failures</p>
          <strong className="source-quality-summary__value">{summary.browserFailures}</strong>
        </article>
        <article className="panel-card">
          <p className="eyebrow">Needs recheck</p>
          <strong className="source-quality-summary__value">{summary.needsRecheck}</strong>
        </article>
        <article className="panel-card">
          <p className="eyebrow">Avg health score</p>
          <strong className="source-quality-summary__value">{summary.average ?? "—"}</strong>
        </article>
      </section>

      {loading ? <p className="intel-meta">Loading image health…</p> : null}
      {error ? <p className="intel-error">{error}</p> : null}

      {!loading && !error ? (
        <div className="source-quality-table-wrap">
          <table className="source-quality-table">
            <thead>
              <tr>
                <th>Source</th>
                <th>Total</th>
                <th>Healthy</th>
                <th>Broken</th>
                <th>Unknown</th>
                <th>Blocked</th>
                <th>Unchecked</th>
                <th>Browser</th>
                <th>Recheck</th>
                <th>Health score</th>
                <th>Last checked</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sources.map((source) => (
                <tr key={source.sourceKey}>
                  <td>
                    <strong>{source.displayName}</strong>
                    <span className="muted">{source.sourceKey}</span>
                  </td>
                  <td>{source.totalImages}</td>
                  <td>{source.healthyImages}</td>
                  <td>{source.brokenImages}</td>
                  <td>{source.unknownImages}</td>
                  <td>{source.blockedImages}</td>
                  <td>{source.uncheckedImages}</td>
                  <td>{source.totalBrowserFailures ?? 0}</td>
                  <td>{source.imagesNeedingRecheck ?? 0}</td>
                  <td>{source.imageHealthScore ?? "—"}</td>
                  <td>
                    {source.lastCheckedAt
                      ? new Date(source.lastCheckedAt).toLocaleString()
                      : "—"}
                  </td>
                  <td className="source-quality-table__actions">
                    <button type="button" onClick={() => openDetail(source.sourceKey)}>
                      View images
                    </button>
                    <button
                      type="button"
                      onClick={() => handleValidateSource(source.sourceKey)}
                      disabled={validating}
                    >
                      Validate
                    </button>
                    {onOpenSourceQuality ? (
                      <button type="button" onClick={() => onOpenSourceQuality()}>
                        Source quality
                      </button>
                    ) : null}
                    {onOpenImageCache ? (
                      <button type="button" onClick={() => onOpenImageCache()}>
                        Image cache
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {selectedKey ? (
        <aside className="source-quality-drawer image-health-drawer" aria-label="Source image health">
          <div className="source-quality-drawer__header">
            <div>
              <p className="eyebrow">Image health</p>
              <h2>{detail?.displayName || selectedKey}</h2>
            </div>
            <button
              type="button"
              onClick={() => {
                setSelectedKey(null);
                setDetail(null);
              }}
            >
              Close
            </button>
          </div>

          {detailLoading ? <p className="intel-meta">Loading images…</p> : null}

          {!detailLoading && detail ? (
            <div className="image-health-drawer__body">
              <div className="image-health-drawer__metrics">
                <StatusBadge label={`${detail.metrics.healthyImages} healthy`} tone="success" />
                <StatusBadge label={`${detail.metrics.brokenImages} broken`} tone="danger" />
                <StatusBadge label={`${detail.metrics.uncheckedImages} unchecked`} tone="neutral" />
                <StatusBadge
                  label={`${detail.metrics.totalBrowserFailures || 0} browser failures`}
                  tone="warning"
                />
                <StatusBadge
                  label={`${detail.metrics.imagesNeedingRecheck || 0} need recheck`}
                  tone="medium"
                />
              </div>

              {detail.recentBrowserFailureEvents?.length ? (
                <section>
                  <h3>Recent browser failures</h3>
                  <ul className="image-health-drawer__events">
                    {detail.recentBrowserFailureEvents.slice(0, 10).map((event) => (
                      <li key={event.eventId}>
                        {event.renderContext} · {event.pageContext || "—"} ·{" "}
                        {new Date(event.receivedAt).toLocaleString()}
                      </li>
                    ))}
                  </ul>
                </section>
              ) : null}

              <ul className="image-health-drawer__list">
                {(detail.images || []).map((image) => (
                  <li key={image.imageId} className="image-health-drawer__item">
                    <div className="image-health-drawer__thumb">
                      {image.healthStatus === "BROKEN" || image.healthStatus === "INVALID_URL" ? (
                        <div className="property-image-placeholder">
                          <span>Unavailable</span>
                        </div>
                      ) : (
                        <img src={image.imageUrl} alt="" loading="lazy" />
                      )}
                    </div>
                    <div className="image-health-drawer__meta">
                      <StatusBadge label={image.healthStatus} tone={healthTone(image.healthStatus)} />
                      <p className="muted">
                        HTTP {image.httpStatus ?? "—"}
                        {image.failureReason ? ` · ${image.failureReason}` : ""}
                      </p>
                      <p className="muted">{image.sourceListingTitle || "Listing"}</p>
                      <p className="image-health-drawer__url">{image.imageUrl}</p>
                      {image.browserFailureCount > 0 ? (
                        <p className="muted">
                          Browser failures: {image.browserFailureCount}
                          {image.browserLastFailureAt
                            ? ` · last ${new Date(image.browserLastFailureAt).toLocaleString()}`
                            : ""}
                          {image.needsRecheck ? " · needs recheck" : ""}
                        </p>
                      ) : null}
                      <div className="source-quality-table__actions">
                        <button type="button" onClick={() => handleRecheckImage(image.imageId)}>
                          Recheck
                        </button>
                        <button type="button" onClick={() => handleOverride(image.imageId, "HEALTHY")}>
                          Mark healthy
                        </button>
                        <button type="button" onClick={() => handleOverride(image.imageId, "BROKEN")}>
                          Mark broken
                        </button>
                        {image.browserFailureCount > 0 ? (
                          <button
                            type="button"
                            onClick={() => handleClearBrowserFailures(image.imageId)}
                          >
                            Clear browser failures
                          </button>
                        ) : null}
                        <a href={image.imageUrl} target="_blank" rel="noreferrer">
                          Open image
                        </a>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </aside>
      ) : null}
    </div>
  );
};

export default ImageHealthPage;
